import Head from "next/head"
import { useContext } from "react"
import { ConfigStateContext } from "shared-components/Contexts/Configurations/context"
import { usePriceTag } from "./PriceTag/PriceTag.hook"
import { cleanHtmlFromString, sanitizeHtml } from "@Utils/Functions"

// Se omite todo campo sin dato (regla de la auditoría SEO §5.1: nunca placeholders).
const sinVacios = obj =>
    Object.fromEntries(
        Object.entries(obj).filter(
            ([, v]) => v !== null && v !== undefined && v !== "" && !(Array.isArray(v) && v.length === 0)
        )
    )

// La UI muestra «Consultar» bajo este umbral (PriceTag/web.tsx) — el markup replica ese gate.
const PRECIO_MINIMO_VISIBLE = 5

const numeroONada = v => {
    if (v === null || v === undefined || v === "") return undefined
    const n = Number(v)
    return Number.isFinite(n) && n >= 0 ? n : undefined
}

// property_type.id → subtipo de Accommodation (schema.org); el resto queda en el genérico.
const tiposSchema = {
    "1": "House", "15": "House", "16": "House", "17": "House",
    "2": "Apartment", "14": "Apartment",
    "18": "Room",
}

export const FichaStructData = ({ prop, mode }) => {
    const { main_domain, country_code } = useContext(ConfigStateContext)
    const { data: priceData } = usePriceTag({
        mode: mode,
        ...prop,
    })

    const currenciesCodes = {
        "1": "USD", "2": "UYU", "3": "PYG",
        "4": "COP", "5": "BOB", "6": "PEN",
        "7": "UYR", "8": "UYI", "9": "BRL",
        "10": "ARS", "11": "EUR",
    }

    const price = priceData?.price?.amount
    const priceCurrency = currenciesCodes[priceData?.price?.currency?.id]
    const activo = prop?.active !== false && prop?.active !== 0

    const offers =
        activo && priceCurrency && !priceData?.price?.hidePrice && Number(price) >= PRECIO_MINIMO_VISIBLE
            ? {
                  "@type": "Offer",
                  "price": price,
                  "priceCurrency": priceCurrency,
                  "availability": "https://schema.org/InStock",
              }
            : undefined

    const address = sinVacios({
        "@type": "PostalAddress",
        "streetAddress": prop?.showAddress ? prop?.address : undefined,
        "addressLocality":
            prop?.locations?.city?.[0]?.name ||
            prop?.locations?.locality?.[0]?.name ||
            prop?.locations?.neighbourhood?.[0]?.name,
        "addressRegion": prop?.locations?.state?.[0]?.name,
        "addressCountry": /^[A-Z]{2}$/.test(country_code || "") ? country_code : undefined,
    })
    // Un PostalAddress solo con el país de la marca no aporta: se exige algún dato del aviso.
    const addressConDato = Object.keys(address).length > (address.addressCountry ? 2 : 1)

    const lat = parseFloat(prop?.latitude)
    const lng = parseFloat(prop?.longitude)
    // Con pointType el mapa muestra zona aproximada: no se publica la coordenada exacta.
    const geo =
        !prop?.pointType && Number.isFinite(lat) && Number.isFinite(lng) && !(lat === 0 && lng === 0)
            ? {
                  "@type": "GeoCoordinates",
                  "latitude": lat,
                  "longitude": lng,
              }
            : undefined

    const galeria = prop?.images?.map(i => i?.image).filter(Boolean) || []
    const images = galeria.length > 0 ? galeria : prop?.img

    const url =
        typeof prop?.link === "string" && prop.link.length > 0
            ? "https://" + main_domain + (prop.link.startsWith("/") ? "" : "/") + prop.link
            : undefined

    const m2 = numeroONada(prop?.m2)
    const nombre = prop?.title?.replace(/\s*\[No disponible\]\s*$/, "")

    // El inmueble físico como entidad propia (mainEntity), estilo homes.com:
    // RealEstateListing es la página del aviso; dormitorios/baños/superficie/dirección/geo
    // pertenecen al Accommodation.
    const inmueble = sinVacios({
        "@type": tiposSchema[prop?.property_type?.id] || "Accommodation",
        "name": nombre,
        "numberOfBedrooms": numeroONada(prop?.bedrooms),
        "numberOfBathroomsTotal": numeroONada(prop?.bathrooms),
        "floorSize":
            m2 > 0
                ? { "@type": "QuantitativeValue", "value": m2, "unitCode": "MTK" }
                : undefined,
        "address": addressConDato ? address : undefined,
        "geo": geo,
    })
    // Sin ningún dato físico (solo @type y name) el sub-objeto no aporta: se omite.
    const mainEntity = Object.keys(inmueble).length > (inmueble.name ? 2 : 1) ? inmueble : undefined

    const jsonData = sinVacios({
        "@context": "https://schema.org",
        // El doble tipo solo con oferta: un Product sin offers es inválido para Google
        // (Search Console lo marcaría en la ficha de cada aviso «Consultar»).
        "@type": offers ? ["RealEstateListing", "Product"] : "RealEstateListing",
        "name": nombre,
        "url": url,
        "image": images,
        "description": sanitizeHtml(cleanHtmlFromString(prop?.description)),
        "datePosted": typeof prop?.created_at === "string" ? prop.created_at.slice(0, 10) : undefined,
        "dateModified": typeof prop?.updated_at === "string" ? prop.updated_at.slice(0, 10) : undefined,
        "offers": offers,
        "mainEntity": mainEntity,
    })

    return <>
        <Head>
             <script
                type="application/ld+json"
                dangerouslySetInnerHTML={{
                    // El escape de < evita que un </script> inyectado en la data corte el bloque (XSS).
                    __html: JSON.stringify(jsonData).replace(/</g, "\\u003c"),
                }}></script>
        </Head>
    </>
}
